/**
 * Permission catalog + builtin role definitions.
 *
 * Hybrid model:
 *   - Permission KEYS (the `Permission` union below) are a typed compile-time
 *     constant. Same type imported by Lambda code via the layer copy.
 *   - Builtin role definitions (the `BUILTIN_ROLE_DEFINITIONS` array) seed
 *     the platform_roles + tenant_roles DynamoDB tables on first deploy.
 *     They are ALSO the code-embedded fallback used by the layer's
 *     hasPermission() resolver if the DDB scan ever fails — the system
 *     never fails open or fully closed.
 *   - Custom roles created by super_admin at runtime live ONLY in DDB.
 *     hasPermission() consults the DDB catalog first; code fallback is for
 *     resilience only.
 *
 * Backend Lambdas re-check every action server-side via the layer's
 * hasPermission(); this module is the UI gate and the catalog source.
 *
 * Cross-family enforcement: backend Lambdas reject platform roles from
 * tenant-scoped endpoints, and tenant roles from platform endpoints. The
 * frontend's ProtectedRoute + nav routing match this.
 */

import type { UserRole } from '@/types';

// ─── Permission catalog ─────────────────────────────────────────────────────

export type Permission =
  // ─── Tenant permissions (used at /org/*) ─────────────────────────────────

  // Identity management
  | 'identity.view'
  | 'identity.create'
  | 'identity.edit'
  | 'identity.delete'
  | 'identity.tag'

  // Provider integrations
  | 'provider.view'
  | 'provider.connect'
  | 'provider.disconnect'
  | 'provider.sync_now'

  // AI Analysis insights
  | 'aiAnalysis.view'
  | 'aiAnalysis.dismiss'
  | 'aiAnalysis.resolve'

  // Token observability
  | 'tokenObservability.view'
  | 'tokenObservability.set_budget'

  // Audit log
  | 'audit.view'
  | 'audit.export'

  // Tenant settings + members
  | 'tenant.view_settings'
  | 'tenant.edit_settings'
  | 'tenant.invite_user'
  | 'tenant.change_role'
  | 'tenant.disable_user'
  | 'tenant.revoke_session'
  | 'tenant.approve_support_access'
  | 'tenant.revoke_support_access'

  // Tenant-side role management (Phase 1.6.2)
  | 'tenant.view_roles'
  | 'tenant.edit_roles'

  // EU AI Act module (Phase 3)
  // Splitting view/manage rather than CRUD-per-field — compliance teams
  // typically need read-everything access, while AI system owners only
  // manage their own systems. Server-side enforcement checks both flags
  // for any write.
  | 'aiAct.view'
  | 'aiAct.manage'
  | 'aiAct.generate_report'

  // ─── Platform permissions (used at /admin/*) ─────────────────────────────

  // Tenants (aggregate views only — never tenant content)
  | 'platform.view_tenants'
  | 'platform.suspend_tenant'
  | 'platform.create_tenant'

  // Subscriptions + waitlist (write actions)
  | 'platform.assign_subscription'
  | 'platform.approve_waitlist'
  | 'platform.reject_waitlist'

  // Platform users + roles
  | 'platform.view_users'
  | 'platform.invite_user'
  | 'platform.change_user_role'
  | 'platform.disable_user'
  | 'platform.delete_user'
  | 'platform.view_roles'
  | 'platform.edit_roles'

  // Connector catalog (platform-curated integration definitions)
  | 'platform.view_connectors'
  | 'platform.edit_connectors'

  // Tenant admin assignment (super admin promotes/demotes within a tenant)
  | 'platform.assign_tenant_admin'

  // Platform audit + insights (reads)
  | 'platform.view_audit'
  | 'platform.view_insights'
  | 'platform.view_billing'

  // Break-glass support access
  | 'platform.request_support_access'   // support_engineer can request a grant
  | 'platform.break_glass_support_access' // super_admin can grant without customer approval

  // Platform settings
  | 'platform.edit_settings'
  // Per-tenant rate-limit overrides (Phase 4)
  | 'platform.manage_limits'

  // Founder workspace (Phase 4) — leadership-only editable content for
  // Strategy / Pitch Deck / Advisor. Intentionally separate namespace
  // from platform.* so founders never get tenant-data permissions by
  // accident.
  | 'founder.view'
  | 'founder.edit_advisor'
  | 'founder.edit_strategy'
  | 'founder.edit_pitchdeck'
  | 'founder.notes_view'
  | 'founder.notes_edit'
  | 'founder.tracker_view'
  | 'founder.tracker_edit'
  | 'founder.financial_view'
  | 'founder.financial_edit';

// ─── Builtin permission sets ────────────────────────────────────────────────

// Tenant
const TENANT_READ_ONLY: readonly Permission[] = [
  'identity.view',
  'provider.view',
  'aiAnalysis.view',
  'tokenObservability.view',
  'audit.view',
  'tenant.view_settings',
  'tenant.view_roles',
  // Compliance teams want read access to AI Act records even when they
  // don't own them — so view + report generation are baseline tenant reads.
  'aiAct.view',
  'aiAct.generate_report',
];

const TENANT_ANALYST: readonly Permission[] = [
  ...TENANT_READ_ONLY,
  'identity.tag',
  'provider.sync_now',
  'aiAnalysis.dismiss',
  'aiAnalysis.resolve',
];

const TENANT_IDENTITY_READER: readonly Permission[] = [
  'identity.view',
  'audit.view',
  'tenant.view_settings',
  'tenant.view_roles',
];

const TENANT_IDENTITY_ADMIN: readonly Permission[] = [
  ...TENANT_IDENTITY_READER,
  'identity.create',
  'identity.edit',
  'identity.delete',
  'identity.tag',
];

const TENANT_PROVIDER_ADMIN: readonly Permission[] = [
  'identity.view',
  'provider.view',
  'provider.connect',
  'provider.disconnect',
  'provider.sync_now',
  'audit.view',
  'tenant.view_settings',
  'tenant.view_roles',
];

const TENANT_ORG_AUDITOR: readonly Permission[] = [
  ...TENANT_READ_ONLY,
  'audit.export',
];

const TENANT_ADMIN: readonly Permission[] = [
  ...TENANT_ANALYST,
  'identity.create',
  'identity.edit',
  'identity.delete',
  'provider.connect',
  'provider.disconnect',
  'tokenObservability.set_budget',
  'audit.export',
  'tenant.edit_settings',
  'tenant.invite_user',
  'tenant.change_role',
  'tenant.disable_user',
  'tenant.revoke_session',
  'tenant.approve_support_access',
  'tenant.revoke_support_access',
  'tenant.edit_roles',
  // Full AI Act management: classify systems, edit risk register,
  // publish disclosures.
  'aiAct.manage',
];

// Platform
const PLATFORM_AUDITOR_READS: readonly Permission[] = [
  'platform.view_tenants',
  'platform.view_audit',
  'platform.view_insights',
  'platform.view_billing',
  'platform.view_users',
  'platform.view_roles',
  'platform.view_connectors',
];

const PLATFORM_BILLING_READER: readonly Permission[] = [
  'platform.view_tenants',
  'platform.view_billing',
];

const PLATFORM_BILLING_ADMIN: readonly Permission[] = [
  ...PLATFORM_BILLING_READER,
  'platform.assign_subscription',
];

const PLATFORM_IAM_READER: readonly Permission[] = [
  'platform.view_users',
  'platform.view_roles',
];

const PLATFORM_IAM_ADMIN: readonly Permission[] = [
  ...PLATFORM_IAM_READER,
  'platform.invite_user',
  'platform.change_user_role',
  'platform.disable_user',
  'platform.delete_user',
  'platform.assign_tenant_admin',
  'platform.edit_connectors',
];

const PLATFORM_SECURITY_READER: readonly Permission[] = [
  'platform.view_tenants',
  'platform.view_audit',
  'platform.view_insights',
];

const PLATFORM_SECURITY_ADMIN: readonly Permission[] = [
  ...PLATFORM_SECURITY_READER,
  'platform.break_glass_support_access',
  'platform.suspend_tenant',
];

const PLATFORM_SUPPORT_ENGINEER: readonly Permission[] = [
  ...PLATFORM_AUDITOR_READS,
  'platform.request_support_access',
];

const PLATFORM_SUPER_ADMIN: readonly Permission[] = [
  ...PLATFORM_AUDITOR_READS,
  'platform.suspend_tenant',
  'platform.create_tenant',
  'platform.assign_subscription',
  'platform.approve_waitlist',
  'platform.reject_waitlist',
  'platform.invite_user',
  'platform.change_user_role',
  'platform.disable_user',
  'platform.delete_user',
  'platform.assign_tenant_admin',
  'platform.break_glass_support_access',
  'platform.edit_settings',
  'platform.edit_roles',
  'platform.edit_connectors',
  'platform.manage_limits',
  // Founder pages are visible + editable by super_admin too (shared routes).
  'founder.view',
  'founder.edit_advisor',
  'founder.edit_strategy',
  'founder.edit_pitchdeck',
  'founder.notes_view',
  'founder.notes_edit',
  'founder.tracker_view',
  'founder.tracker_edit',
  'founder.financial_view',
  'founder.financial_edit',
];

// Founder role: leadership-only access to Founder Home + Strategy +
// Pitch Deck + Advisor. NO platform.* permissions — intentionally
// isolated from tenants, billing, audit, users, roles.
const PLATFORM_FOUNDER: readonly Permission[] = [
  'founder.view',
  'founder.edit_advisor',
  'founder.edit_strategy',
  'founder.edit_pitchdeck',
  'founder.notes_view',
  'founder.notes_edit',
  'founder.tracker_view',
  'founder.tracker_edit',
  'founder.financial_view',
  'founder.financial_edit',
];

// ─── Builtin role definitions ───────────────────────────────────────────────
//
// This is the seed catalog. The bootstrap-playground Lambda writes these rows
// into the platform_roles + tenant_roles DynamoDB tables on first deploy
// (conditional put — never overwrites custom edits).
//
// Re-export shape:
//   - `family` distinguishes which DDB table the row belongs to.
//   - `builtin = true` is implied for all rows here. The layer resolver
//     stamps it onto the persisted row.
//   - Order matters for the UI: items appear in this order in the role list.

export interface RoleDefinition {
  family: 'platform' | 'tenant';
  role_key: UserRole;
  label: string;
  description: string;
  permission_keys: readonly Permission[];
}

export const BUILTIN_ROLE_DEFINITIONS: readonly RoleDefinition[] = [
  // ── Platform builtins ────────────────────────────────────────────────────
  {
    family: 'platform',
    role_key: 'super_admin',
    label: 'Super Admin',
    description: 'Full platform control: tenants, subscriptions, waitlist, billing, user + role management, break-glass.',
    permission_keys: PLATFORM_SUPER_ADMIN,
  },
  {
    family: 'platform',
    role_key: 'auditor',
    label: 'Auditor',
    description: 'Read-only access to every platform-level surface. No mutations.',
    permission_keys: PLATFORM_AUDITOR_READS,
  },
  {
    family: 'platform',
    role_key: 'support_engineer',
    label: 'Support Engineer',
    description: 'Aggregates only by default. Can request a time-boxed read grant into a specific tenant via the break-glass flow.',
    permission_keys: PLATFORM_SUPPORT_ENGINEER,
  },
  {
    family: 'platform',
    role_key: 'billing_admin',
    label: 'Billing Admin',
    description: 'Manage tenant subscriptions, tiers, and plan levels. Cannot touch users, roles, audit, or support access.',
    permission_keys: PLATFORM_BILLING_ADMIN,
  },
  {
    family: 'platform',
    role_key: 'billing_reader',
    label: 'Billing Reader',
    description: 'Read-only view of billing dashboards and per-tenant subscription state.',
    permission_keys: PLATFORM_BILLING_READER,
  },
  {
    family: 'platform',
    role_key: 'iam_admin',
    label: 'IAM Admin',
    description: 'Manage platform users (invite, role change, disable, delete) and tenant-admin assignment. No billing or audit powers.',
    permission_keys: PLATFORM_IAM_ADMIN,
  },
  {
    family: 'platform',
    role_key: 'iam_reader',
    label: 'IAM Reader',
    description: 'Read-only view of the platform users list and role catalog.',
    permission_keys: PLATFORM_IAM_READER,
  },
  {
    family: 'platform',
    role_key: 'security_admin',
    label: 'Security Admin',
    description: 'Read audit + insights, suspend tenants, exercise break-glass support access during incidents.',
    permission_keys: PLATFORM_SECURITY_ADMIN,
  },
  {
    family: 'platform',
    role_key: 'security_reader',
    label: 'Security Reader',
    description: 'Read-only audit log + platform insights. Cannot suspend tenants or break-glass.',
    permission_keys: PLATFORM_SECURITY_READER,
  },
  {
    family: 'platform',
    role_key: 'founder',
    label: 'Founder',
    description: 'Leadership-only access to Founder Home, Strategy, Pitch Deck, and Advisor. Cannot see tenants, billing, audit, or any operational surface.',
    permission_keys: PLATFORM_FOUNDER,
  },

  // ── Tenant builtins ──────────────────────────────────────────────────────
  {
    family: 'tenant',
    role_key: 'org_admin',
    label: 'Org Admin',
    description: 'Full control of the tenant: identities, providers, members, billing settings, role catalog, audit export, support-access approval.',
    permission_keys: TENANT_ADMIN,
  },
  {
    family: 'tenant',
    role_key: 'org_auditor',
    label: 'Org Auditor',
    description: 'Read-only across the tenant + audit log export. For compliance reviewers.',
    permission_keys: TENANT_ORG_AUDITOR,
  },
  {
    family: 'tenant',
    role_key: 'security_analyst',
    label: 'Security Analyst',
    description: 'Day-to-day investigation: tag identities, sync providers, dismiss/resolve AI findings.',
    permission_keys: TENANT_ANALYST,
  },
  {
    family: 'tenant',
    role_key: 'identity_admin',
    label: 'Identity Admin',
    description: 'Full identity CRUD within the tenant. No provider connect/disconnect, no member or role management.',
    permission_keys: TENANT_IDENTITY_ADMIN,
  },
  {
    family: 'tenant',
    role_key: 'identity_reader',
    label: 'Identity Reader',
    description: 'Read-only access to identities + audit log. Useful for compliance integrators.',
    permission_keys: TENANT_IDENTITY_READER,
  },
  {
    family: 'tenant',
    role_key: 'provider_admin',
    label: 'Provider Admin',
    description: 'Connect, disconnect, and sync identity providers. Read-only access to the identities they bring in.',
    permission_keys: TENANT_PROVIDER_ADMIN,
  },
  {
    family: 'tenant',
    role_key: 'viewer',
    label: 'Viewer',
    description: 'Read-only access to everything in the tenant.',
    permission_keys: TENANT_READ_ONLY,
  },
];

// Quick lookup map used by the synchronous fallback resolver below.
const ROLE_PERMISSIONS: Record<UserRole, readonly Permission[]> = Object.fromEntries(
  BUILTIN_ROLE_DEFINITIONS.map((r) => [r.role_key, r.permission_keys])
) as Record<UserRole, readonly Permission[]>;

// ─── Role family helpers ────────────────────────────────────────────────────

const PLATFORM_ROLE_KEYS: readonly UserRole[] = BUILTIN_ROLE_DEFINITIONS
  .filter((r) => r.family === 'platform')
  .map((r) => r.role_key);

export function isPlatformRole(role: UserRole | string | null | undefined): boolean {
  return role ? PLATFORM_ROLE_KEYS.includes(role as UserRole) : false;
}

export function isTenantRole(role: UserRole | string | null | undefined): boolean {
  if (!role) return false;
  // Unknown role strings (custom roles from DDB) are tenant by default at the
  // UI gate level — backend re-checks family server-side.
  return !PLATFORM_ROLE_KEYS.includes(role as UserRole);
}

// ─── Resolver (synchronous, code-embedded) ──────────────────────────────────
//
// This is the UI-side resolver — synchronous, no DDB, only knows about
// builtin roles. The Lambda layer has its own async version that reads from
// DDB with a TTL cache + this same builtin set as fallback.
//
// For custom (non-builtin) roles, this returns false at the UI; the backend
// is authoritative. The Phase 1.6.2 frontend rewrite fetches the live role
// catalog from /admin/platform-roles and /org/roles so the UI also reflects
// custom roles' permissions.

export function hasPermission(
  role: UserRole | string | null | undefined,
  permission: Permission
): boolean {
  if (!role) return false;
  // super_admin is a wildcard at every layer. Keeps the UI gate honest if
  // their DDB row hasn't been reconciled with newly-added permission keys
  // yet. Matches services/layers/lambda-utils/src/index.ts hasPermission().
  if (role === 'super_admin') return true;
  const set = ROLE_PERMISSIONS[role as UserRole];
  if (!set) return false;
  return set.includes(permission);
}

export function permissionsFor(role: UserRole | string | null | undefined): readonly Permission[] {
  if (!role) return [];
  // For super_admin we still return the catalog list so the UI can render a
  // matrix; enforcement goes through hasPermission() which short-circuits.
  return ROLE_PERMISSIONS[role as UserRole] ?? [];
}
